/* eslint-disable @typescript-eslint/no-explicit-any */
import '@tock/tokens/dist/bento.scss';
import { AddTrustArcScript } from 'shared/components/cookie-consent/AddTrustArcScript';
import { AppContextProvider } from '@tock/money';
import { AsyncRenderProvider } from 'contexts/asyncRender';
import { ConsentRestrictedScripts } from 'components/consent-restricted-scripts/ConsentRestrictedScripts';
import { CssBaseline } from '@material-ui/core';
import { DndProvider, TouchTransition } from 'react-dnd-multi-backend';
import { ElementProvider } from '@tock/payment-elements'; // Upgrading react-dnd-multi-backend can have a silent error for a circular JSON object appear in staging that does not appear in local or PR environments. Attempted with v6 -> v8
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { FeatureFlagContext } from '@tock/shared/utils/featureFlagContext';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { IntlProvider } from 'react-intl';
import { ManageCookieConsentState } from '@tock/shared/components/cookie-consent/ManageCookieConsentState';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { Redirect, Route, RouteComponentProps, Router, Switch } from 'react-router-dom';
import { ScheduledInventoryConfigContext } from 'contexts/scheduledInventoryConfigContext';
import { TaxStrategyContextProvider } from 'contexts/taxStrategyContext';
import { TouchBackend } from 'react-dnd-touch-backend';
import { UserActionTracker } from 'utils/analytics/UserActionTracker';
import { asyncComponent } from '@tock/shared/utils/asyncComponent';
import { cancelJobsToNative } from 'utils/printer/networkPrinterUtils';
import { clearEventStreamState } from 'utils/service/localStorageUtils';
import { createBrowserHistory } from 'history';
import { dashboard } from 'types';
import { debounce, drop, isEmpty, throttle } from 'es-toolkit/compat';
import { defaultMemoize } from 'reselect';
import {
  getDashboardPreferencesSelector,
  isDashboardPreferencesInitializedSelector,
  isHdModeEnabledSelector,
} from 'selectors/dashboardPreferenceSelectors';
import { getTheme } from 'theme/Theme';
import { hasEngRoleSelector } from 'selectors/rolesSelectors';
import { logger } from '@tock/logging';
import { resizeCallback } from '@tock/shared/utils/responsive';
import Analytics from 'utils/Analytics';
import AppSettings from 'settings/AppSettings';
import BaseComponent from '@tock/shared/components/BaseComponent';
import CustomDragLayer from 'components/CustomDragLayer';
import DashboardProductFeatureProvider from 'components/product/DashboardProductFeatureProvider';
import ErrorBoundary from 'utils/ErrorBoundary';
import ProtectedRoute from 'routes/ProtectedRoute';
import ReduxStateMonitors from 'components/ReduxStateMonitors';
import RoleContextProvider from 'components/product/RoleContextProvider';
import Toaster from 'components/Toaster';
import UnprotectedRoute from 'routes/UnprotectedRoute';
import confirm from 'shared/utils/confirm';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';
import pluralize from 'pluralize';

const AuthenticatedRoute = asyncComponent(() => import('routes/AuthenticatedRoute'));
const ForgotPasswordRoute = asyncComponent(
  () => import('routes/unauthenticated/ForgotPasswordRoute')
);
const MfaLoginRoute = asyncComponent(() => import('routes/unauthenticated/MfaLoginRoute'));
const MfaVerifyRoute = asyncComponent(() => import('routes/unauthenticated/MfaVerifyRoute'));
const FacebookAppStoreLoginRoute = asyncComponent(
  () => import('routes/unauthenticated/FacebookAppStoreLoginRoute')
);
const ResetPasswordRoute = asyncComponent(
  () => import('routes/unauthenticated/ResetPasswordRoute')
);

const SignupRoute = asyncComponent(() => import('routes/unauthenticated/SignupRoute'));

const GraphiQLHomeRoute = asyncComponent(() => import('graphiql/GraphiQLHome'));

const history = createBrowserHistory({
  getUserConfirmation(message, callback) {
    return confirm
      .onTransition(message)
      .then(() => {
        callback(true);
      })
      .catch(() => {
        callback(false);
      });
  },
});

history.listen((location) => {
  Analytics.transitionToPage(location.pathname);
});

interface ConnectProps extends DefaultConnectProps {
  elmDecodeError?: string | null;
  elmError?: string;
  isEng: boolean;
  printerProgressMessage?: string;
  toast: StoreType['toast'];
  dashboardPreferences: dashboard.DashboardPreferences;
  isHdModeEnabled: boolean;
  isPreferencesInitialized: boolean;
  persistedUi: StoreType['persistedUi'];
}

type Props = ConnectProps;

const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend,
      options: { enableMouseEvents: true, delay: 1, touchSlop: 12 },
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true, delay: 1, touchSlop: 12 },
      preview: false,
      transition: TouchTransition,
    },
  ],
};

class Application extends BaseComponent<Props> {
  private onResize = throttle(() => this.forceUpdate(), 150);

  constructor(props: Props) {
    super(props);
    this.sendDecodeErrorToRollbar = debounce(this.sendDecodeErrorToRollbar, 500);
    this.setPluralRules();
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);

    this.loadGlobalFeaturesIfUnauthenticated();
    this.loadDashboardPreferences();
    this.handleSwitchToHighDensity();
    this.updateDarkMode();
  }

  componentDidUpdate(prevProps: Props) {
    const { printerProgressMessage, actions, elmDecodeError, elmError, app } = this.props;

    if (prevProps.app.activeBusiness?.id !== app.activeBusiness?.id) {
      this.loadDashboardPreferences();
    }
    if (prevProps.isHdModeEnabled !== this.props.isHdModeEnabled) {
      this.handleSwitchToHighDensity();
    }

    if (printerProgressMessage && prevProps.printerProgressMessage !== printerProgressMessage) {
      actions.toast.showInfo(printerProgressMessage, {
        linkText: 'Cancel',
        onClear: actions.networkPrinters.closePrinterProgress,
        onClickLink: cancelJobsToNative,
      });
    }

    if (elmDecodeError && prevProps.elmDecodeError !== elmDecodeError) {
      logger.error(elmDecodeError);
      this.sendDecodeErrorToRollbar(elmDecodeError);
      actions.toast.showError(
        `Something with Tock went wrong. You may continue using Dashboard but if the problem persists please refresh the page or contact us to report the issue.`,
        {
          onClear: () => {
            if (window.elmApp) {
              this.props.actions.elmApp.clearToast();
            }
          },
        }
      );
    }

    if (elmError && prevProps.elmError !== elmError) {
      actions.toast.showError(elmError, {
        onClear: () => {
          if (window.elmApp) {
            this.props.actions.elmApp.clearToast();
          }
        },
      });
    }

    this.updateDarkMode();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    this.onResize.cancel();
  }

  loadGlobalFeaturesIfUnauthenticated() {
    if (!this.props.app.isAuthenticated) {
      this.props.actions.features.fetchGlobalFeatures();
    }
  }

  loadDashboardPreferences() {
    const {
      actions,
      app: { activeBusiness },
      isPreferencesInitialized,
    } = this.props;

    if (activeBusiness?.id && !isPreferencesInitialized) {
      actions.dashboardPreferences.fetch(activeBusiness.id);
    }
  }

  getIsDarkMode() {
    const { dashboardPreferences } = this.props;
    if (
      AppSettings.CHECKIN ||
      AppSettings.DELIVERY ||
      window.location.pathname.startsWith('/join') ||
      window.location.pathname.startsWith('/schedule') ||
      window.location.pathname.startsWith('/control-panel/configuration/walkthrough')
    ) {
      return false;
    }
    return dashboardPreferences.darkMode;
  }

  updateDarkMode() {
    if (this.getIsDarkMode()) {
      document.body.classList.add('DarkMode');
      document.body.setAttribute('data-colormode', 'dark');
    } else {
      document.body.classList.remove('DarkMode');
      document.body.setAttribute('data-colormode', 'light');
    }
    return;
  }

  handleSwitchToHighDensity() {
    const { persistedUi, isHdModeEnabled } = this.props;
    resizeCallback(persistedUi.mobileModeEnabled, isHdModeEnabled);
  }

  handleClearToast() {
    this.props.actions.toast.clear();
    // For the moment, `elmApp` only exists in the dashboard's Application if we're on the service
    // route, so we need to check that it exists before clearing the error. -- YK 2017-10-25
    if (window.elmApp) {
      this.props.actions.elmApp.clearToast();
    }
  }

  sendDecodeErrorToRollbar(decodeError: any) {
    if (!decodeError) {
      return;
    }
    window.LOGGER?.warn('Elm Decoding Error', { decodeError });
    clearEventStreamState();
  }

  dropHashRoute({ location }: RouteComponentProps) {
    if (location.pathname === '/' && !isEmpty(location.hash) && location.hash.startsWith('#/')) {
      const [hashLocation, hashSearch] = location.hash.split('?');
      const hashPieces = hashLocation.split('/');
      const newPathname = drop(hashPieces).join('/');
      const to: RouteComponentProps['location'] = {
        ...location,
        search: hashSearch,
        pathname: `/${newPathname}`,
        hash: '',
      };
      return <Redirect to={to} />;
    }
    return null;
  }

  getAppTheme = defaultMemoize((type: 'light' | 'dark') => getTheme(type));

  setPluralRules() {
    pluralize.addIrregularRule('does', 'do');
  }

  render() {
    const { actions, app, currency, features, globalFeatures, isEng, locale, timeZone } =
      this.props;

    const featuresToUse = isEmpty(features) ? globalFeatures : features;

    const isDarkMode = this.getIsDarkMode();

    const theme = this.getAppTheme(isDarkMode ? 'dark' : 'light');

    return (
      <MuiThemeProvider theme={theme}>
        <EmotionThemeProvider theme={theme}>
          <DndProvider options={HTML5toTouch}>
            <AppContextProvider
              activeBusiness={app ? app.activeBusiness : undefined}
              activeAuth={app ? app.activeAuth : undefined}
              currency={currency}
              locale={locale}
              features={featuresToUse}
              history={history}
              timeZone={timeZone}
            >
              <FeatureFlagContext.Provider value={featuresToUse}>
                <ManageCookieConsentState
                  onUpdateCookieConsentLevel={actions.cookieConsent.updateCookieConsentLevel}
                />
                {app.nativeAppVersion === '' && !AppSettings.CHECKIN && !AppSettings.DELIVERY ? (
                  <AddTrustArcScript />
                ) : null}
                <ReduxStateMonitors />
                <ScheduledInventoryConfigContext.Provider
                  value={app.activeBusiness?.scheduledInventoryConfig}
                >
                  <ElementProvider stripePublicKey={AppSettings.STRIPE_PUBLIC_KEY}>
                    <IntlProvider locale={app.locale}>
                      <TaxStrategyContextProvider>
                        <DashboardProductFeatureProvider
                          activeProductFeatureSet={app.activeProductFeatureSet}
                        >
                          <RoleContextProvider>
                            <AsyncRenderProvider>
                              <Router history={history}>
                                <ConsentRestrictedScripts />
                                <ErrorBoundary>
                                  <CssBaseline />
                                  <div className="Wrapper">
                                    <CustomDragLayer />
                                    <UserActionTracker />
                                    <Route path="/" render={this.dropHashRoute} />
                                    <Switch>
                                      <Route
                                        component={FacebookAppStoreLoginRoute}
                                        path="/login/fbe/appstore"
                                      />

                                      <UnprotectedRoute
                                        component={ForgotPasswordRoute}
                                        isAuthenticated={app.isAuthenticated}
                                        path="/forgot"
                                      />

                                      <UnprotectedRoute
                                        component={MfaLoginRoute}
                                        isAuthenticated={app.isAuthenticated}
                                        path="/login"
                                      />

                                      <UnprotectedRoute
                                        component={MfaVerifyRoute}
                                        isAuthenticated={app.isAuthenticated}
                                        path="/verify"
                                      />

                                      <Route component={ResetPasswordRoute} path="/reset" />

                                      <Route component={SignupRoute} path="/signup" />

                                      {isEng && (
                                        <Route component={GraphiQLHomeRoute} path="/graphiql" />
                                      )}

                                      <ProtectedRoute
                                        component={AuthenticatedRoute}
                                        protection={{
                                          requireThat: app.isAuthenticated,
                                          elseRedirectTo:
                                            isEmpty(location.hash) ||
                                            !location.hash.startsWith('#/')
                                              ? {
                                                  pathname: '/login',
                                                  search: `continueTo=${encodeURIComponent(
                                                    `${location.pathname}${location.search}`
                                                  )}`,
                                                }
                                              : null,
                                        }}
                                        path="/"
                                      />
                                    </Switch>
                                    <Toaster />
                                  </div>
                                </ErrorBoundary>
                              </Router>
                            </AsyncRenderProvider>
                          </RoleContextProvider>
                        </DashboardProductFeatureProvider>
                      </TaxStrategyContextProvider>
                    </IntlProvider>
                  </ElementProvider>
                </ScheduledInventoryConfigContext.Provider>
              </FeatureFlagContext.Provider>
            </AppContextProvider>
          </DndProvider>
        </EmotionThemeProvider>
      </MuiThemeProvider>
    );
  }
}

export default connect<{}, Props & DefaultConnectProps>(Application, (state: StoreType) => {
  const { jobsCompleted, jobsInProgress, jobsSubmitted, showJobProgress } =
    state.networkPrintersNoLocalStorage;

  return {
    elmDecodeError: state.service.app.decodeError,
    elmError: state.service.app.error,
    isEng: hasEngRoleSelector(state),
    printerProgressMessage:
      jobsInProgress && showJobProgress
        ? `Printing ${jobsCompleted + 1} of ${jobsSubmitted} chits...`
        : '',
    toast: state.toast,
    dashboardPreferences: getDashboardPreferencesSelector(state),
    isHdModeEnabled: isHdModeEnabledSelector(state),
    isPreferencesInitialized: isDashboardPreferencesInitializedSelector(state),
    persistedUi: state.persistedUi,
  };
});
